import React, { useState } from 'react';
import ResponsiveContainer from './layout/ResponsiveContainer';
import { useDevice } from '../hooks/useDevice';
import { FaTwitter, FaLinkedin, FaInstagram, FaFacebook } from 'react-icons/fa';
import { enableDemoMode } from '../lib/featureFlags';
import { getApiUrl } from '../lib/getApiUrl';

interface WaitlistProps {
  onCopyrightClick?: () => void;
  onNavigate: (screen: string) => void;
}

const SOCIALS = [
  { name: 'Facebook', url: 'https://www.facebook.com/profile.php?id=61579328875227', icon: <FaFacebook size={28} color="#1877F2" /> },
  { name: 'Instagram', url: 'https://instagram.com/corebids', icon: <FaInstagram size={28} color="#E4405F" /> },
  { name: 'Twitter', url: 'https://twitter.com/corebids', icon: <FaTwitter size={28} color="#1DA1F2" /> },
  { name: 'LinkedIn', url: 'https://linkedin.com/company/corebids', icon: <FaLinkedin size={28} color="#0077B5" /> },
];

const FAQ_CATEGORIES = [
  {
    title: 'About CoreBids',
    faqs: [
      {
        q: 'What is CoreBids?',
        a: 'CoreBids is a next-generation platform connecting homeowners and contractors for roofing and exterior projects. We streamline the bidding, compliance, and project management process for both sides.'
      },
      {
        q: 'When will CoreBids launch?',
        a: 'We are currently in private beta testing and expect to launch soon. Join the waitlist for early access & updates!'
      },
      {
        q: 'Who can join the waitlist?',
        a: 'Contractors and industry professionals interested in early access, feedback, & partnership opportunities.'
      },
      {
        q: 'Can I use the platform on mobile devices?',
        a: 'Absolutely! Our platform is fully responsive and accessible via smartphones and tablets for on-the-go management.'
      }
    ]
  },
  {
    title: 'Getting Started',
    faqs: [
      {
        q: 'How do I create a contractor profile?',
        a: 'Contractors who signup for the waitlist will be able to create a profile with an email link.Signing up is quick—just provide your business info, licenses, and insurance details to get verified.'
      },
      {
        q: 'Is there a fee to join CoreBids?',
        a: 'For our founding users we offer free basic profiles. Options for paid plans that unlock premium leads and features will be available soon.'
      },
      {
        q: 'Does the platform verify contractors?',
        a: 'Yes, we conduct license and insurance checks, background verification, and compliance reviews to ensure quality.'
      }
    ]
  },
  {
    title: 'Bidding & Leads',
    faqs: [
      {
        q: 'How does the bidding process work?',
        a: 'Homeowners post projects with measurements. You are then able to search through those projects that are posted and submit bids directly through the platform. You control your pricing and timelines.'
      },
      {
        q: 'Are the leads exclusive to me?',
        a: 'Some leads are exclusive, while others may be shared with select contractors based on project type and location.'
      },
      {
        q: 'What kind of projects are posted on the platform?',
        a: 'For now, just roofing. To come, projects will range from roofing and siding to windows, gutters, and other exterior construction services.'
      }
    ]
  },
  {
    title: 'Payments & Project Management',
    faqs: [
      {
        q: 'How do I get paid?',
        a: 'Payments are handled securely through the platform with milestone releases to ensure timely and safe transactions.'
      },
      {
        q: 'Can I communicate directly with homeowners?',
        a: 'Yes, the platform supports real-time messaging to discuss project details and updates.'
      },
      {
        q: 'What happens if a project is delayed or canceled?',
        a: 'The platform provides tools for tracking progress and managing changes, including dispute resolution support if needed.'
      }
    ]
  }
];

const Waitlist: React.FC<WaitlistProps> = ({ onCopyrightClick, onNavigate }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set());
  const [openQuestions, setOpenQuestions] = useState<Set<string>>(new Set());

  // Calculator state variables
  const [bids, setBids] = useState(20);
  const [closeRate, setCloseRate] = useState(15);
  const [grossMargin, setGrossMargin] = useState(35);
  const [ticketSize, setTicketSize] = useState(15000);
  const costPerBid = 50;

  // Base calculations
  const totalCost = bids * costPerBid;
  const closedJobs = Math.round(bids * (closeRate / 100));
  const grossSales = closedJobs * ticketSize;
  const netProfit = grossSales * (grossMargin / 100) - totalCost;

  // Projections (30 days, 12 months, 24 months)
  const projections = {
    monthly: { sales: grossSales, spend: totalCost, profit: netProfit },
    yearly: {
      sales: grossSales * 12,
      spend: totalCost * 12,
      profit: netProfit * 12,
    },
    twoYears: {
      sales: grossSales * 24,
      spend: totalCost * 24,
      profit: netProfit * 24,
    },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // CB:BEGIN optional-email-validation
    // Only validate email format if email is provided
    if (email && !email.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }
    // CB:END optional-email-validation

    try {
      const response = await fetch(getApiUrl('/api/waitlist'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email || '', // Send empty string if no email provided
          type: 'CONTRACTOR',
          source: 'waitlist-page'
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        console.log('✅ Successfully joined waitlist:', data);
      } else {
        console.error('❌ Failed to join waitlist:', data);
        alert(data.error || 'Failed to join waitlist. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error joining waitlist:', error);
      alert('Network error. Please check your connection and try again.');
    }
  };

  const onDemo = () => {
    enableDemoMode();
    
    // simulate "live" experience: go to landing
    onNavigate('landing');
  };

  const toggleCategory = (categoryTitle: string) => {
    const newOpenCategories = new Set(openCategories);
    if (newOpenCategories.has(categoryTitle)) {
      newOpenCategories.delete(categoryTitle);
    } else {
      newOpenCategories.add(categoryTitle);
    }
    setOpenCategories(newOpenCategories);
  };

  const toggleQuestion = (questionKey: string) => {
    const newOpenQuestions = new Set(openQuestions);
    if (newOpenQuestions.has(questionKey)) {
      newOpenQuestions.delete(questionKey);
    } else {
      newOpenQuestions.add(questionKey);
    }
    setOpenQuestions(newOpenQuestions);
  };

  const { isMobileLayout } = useDevice();

  return (
    <ResponsiveContainer>
    <div style={{ minHeight: isMobileLayout ? 'auto' : '100svh', paddingBottom: 40 }}>
      {/* Hero Section */}
      <div style={{ color: 'white', padding: isMobileLayout ? '48px 0 32px 0' : '64px 0 48px 0', textAlign: 'center', position: 'relative', overflow: 'hidden', minHeight: isMobileLayout ? 'auto' : '100svh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {/* Demo Mode Button removed */}


        {/* Video Background */}
        {!isMobileLayout && (
          <video
            autoPlay
            loop
            muted
            playsInline
            onError={(e) => {
              console.log('Video failed to load, using fallback background');
              const videoElement = e.target as HTMLVideoElement;
              if (videoElement) {
                videoElement.style.display = 'none';
              }
            }}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: -2
            }}
          >
            <source src="https://raw.githubusercontent.com/dan-corebids/CoreBids-Media/9dbec8143c1605a75d43df73874cf9ad510b0b3c/WaitlistVid.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
        
        {/* Dark Overlay for Text Readability */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: -1
        }} />
        
        {/* Content */}
        <div style={{ position: 'relative', zIndex: 1, maxWidth: '800px', padding: '0 20px' }}>
          <h1 style={{ fontSize: isMobileLayout ? 38 : 54, fontWeight: 900, marginBottom: 18, letterSpacing: -1, lineHeight: 1.1 }}>
            <span style={{ 
              color: '#60a5fa', 
              fontSize: isMobileLayout ? '44px' : '64px',
              fontWeight: 900,
              letterSpacing: '-0.02em'
            }}>CoreBids</span>
            <br />
            Bid More, Win More
          </h1>
          <p style={{ fontSize: isMobileLayout ? 18 : 24, color: '#ffffff', maxWidth: 700, margin: '0 auto', fontWeight: 500, lineHeight: 1.4 }}>
            Open bid projects - complete with measurements - direct to you. Bid instantly on projects through CoreBids. Join the waitlist now for early access & bid before your competition. 
          </p>
          <form onSubmit={handleSubmit} style={{ marginTop: 40, display: 'flex', justifyContent: 'center', gap: 12, flexWrap: 'wrap', animation: 'fadeInUp 1s' }}>
            {/* CB:BEGIN optional-email-field */}
            <input
              type="email"
              inputMode="email"
              placeholder="Enter Email (Optional)"
              value={email}
              onChange={e => setEmail(e.target.value)}
              disabled={submitted}
              style={{ padding: '16px 24px', borderRadius: 8, border: 'none', fontSize: 20, width: 'min(100%, 340px)', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}
            />
            {/* CB:END optional-email-field */}
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <button
                type="submit"
                className="btn btn-primary"
                style={{ padding: isMobileLayout ? '12px 16px' : '16px 40px', fontSize: isMobileLayout ? 16 : 20, borderRadius: 8, fontWeight: 700, background: 'linear-gradient(90deg, #60a5fa 0%, #2563eb 100%)', color: 'white', border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.3)', cursor: 'pointer', transition: 'background 0.2s' }}
                disabled={submitted}
              >
                {submitted ? 'Thank You!' : 'Join Waitlist'}
              </button>
              
              {/* CB:BEGIN demo-button-persistent */}
              {/* Demo Button - To the right of Join Waitlist button */}
              <button
                type="button"
                onClick={() => onNavigate('contractor-landing')}
                style={{
                  padding: isMobileLayout ? '12px 12px' : '16px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)',
                  color: 'white',
                  fontSize: isMobileLayout ? '12px' : '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)';
                }}
              >
                Try Demo
              </button>
              {/* CB:END demo-button-persistent */}
            </div>
          </form>
          {submitted && <div style={{ color: '#4ade80', marginTop: 18, fontSize: 20, fontWeight: 600, animation: 'fadeIn 0.7s' }}>You're on the list! We'll keep you updated.</div>}
      </div>
      
      {/* Scroll For More Indicator */}
      <div 
        onClick={() => {
          const nextSection = document.querySelector('.section') || document.querySelector('[style*="background: white"]');
          if (nextSection) {
            nextSection.scrollIntoView({ behavior: 'smooth' });
          }
        }}
        style={{ 
          position: 'absolute', 
          bottom: '10px', 
          left: '50%', 
          transform: 'translateX(-50%)', 
          color: 'white', 
          fontSize: '16px', 
          fontWeight: '400', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          cursor: 'pointer',
          animation: 'bounce 2s infinite',
          zIndex: 2,
          transition: 'transform 0.2s ease'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateX(-50%) scale(1.05)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateX(-50%) scale(1)';
        }}
      >
        <span>Scroll For More</span>
        <span style={{ fontSize: '20px' }}>↓</span>
      </div>
    </div>

      {/* Product Section */}
      <div style={{ maxWidth: 1000, margin: '56px auto', background: 'white', borderRadius: 20, boxShadow: '0 8px 32px rgba(30,41,59,0.10)', padding: isMobileLayout ? 24 : 48, animation: 'fadeInUp 1.2s' }}>
        <h2 style={{ fontSize: 36, fontWeight: 800, marginBottom: 18, color: '#1e293b' }}>What is CoreBids?</h2>
        <p style={{ fontSize: 20, color: '#374151', marginBottom: 28, fontWeight: 500 }}>
          The Smarter Way to Win More Bids. CoreBids is an open construction marketplace that instantly connects homeowners and contractors to roofing and exterior projects. We simplify bidding, compliance, and project management for both sides with:
        </p>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 20, color: '#2563eb' }}>Ready-to-Go Projects</div>
          <div style={{ color: '#374151', fontSize: 18, fontWeight: 500 }}>Bid on real qualified leads with complete project renderings and measurements, instantly open for bidding on CoreBids. Stop wasting time chasing dead-end jobs.</div>
        </div>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 20, color: '#2563eb' }}>Guaranteed Payment Before You Start</div>
          <div style={{ color: '#374151', fontSize: 18, fontWeight: 500 }}>Every project is paid in full upfront and secured in our escrow. Funds are released as you hit milestones — no more chasing checks, issuing hundreds of invoices, or waiting to get paid.</div>
        </div>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 20, color: '#2563eb' }}>More Bids, More Business</div>
          <div style={{ color: '#374151', fontSize: 18, fontWeight: 500 }}>Spend less time and money hunting for work and more time doing what drives your business: building. CoreBids delivers the work straight to you.</div>
        </div>

      </div>

      {/* How Much Can You Earn? Section */}
      <div
        style={{
          maxWidth: 1200,
          margin: '60px auto',
          padding: isMobileLayout ? '32px 16px' : '60px 32px',
          background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 50%, #1d4ed8 100%)',
          borderRadius: 24,
          border: '3px solid #1e40af',
          boxShadow: '0 20px 60px rgba(30, 58, 138, 0.3)',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background Pattern */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 30% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%)',
          pointerEvents: 'none'
        }}></div>
        
        <h2
          style={{
            fontSize: 'clamp(36px, 5vw, 48px)',
            fontWeight: 800,
            marginBottom: 16,
            color: 'white',
            textAlign: 'center',
            textShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
            position: 'relative',
            zIndex: 1
          }}
        >
          Discover How Much You Can Earn 💰
        </h2>
        


        {/* Calculator Card */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          borderRadius: 20,
          padding: isMobileLayout ? '20px' : '40px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.15), 0 8px 25px rgba(0, 0, 0, 0.1)',
          marginBottom: '40px',
          border: '2px solid rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          position: 'relative',
          zIndex: 1
        }}>

          {/* Bids per Month Slider */}
          <div style={{ marginBottom: 32 }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16
            }}>
              <label style={{ fontSize: 32, fontWeight: 700, color: '#374151' }}>
                Bids per Month:
              </label>
              <span style={{ fontSize: 36, fontWeight: 700, color: '#3b82f6' }}>
                {bids}
              </span>
            </div>
            <input
              type="range"
              min="0"
              max="200"
              value={bids}
              onChange={(e) => setBids(Number(e.target.value))}
              style={{
                width: '100%',
                height: 8,
                borderRadius: 4,
                background: '#e5e7eb',
                outline: 'none',
                cursor: 'pointer'
              }}
            />
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 12,
              color: '#6b7280',
              marginTop: 8
            }}>
              <span>0</span>
              <span>200</span>
            </div>
          </div>

          {/* Adjustable Inputs */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 24,
            marginBottom: 32
          }}>
            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: '#374151'
              }}>
                Close Rate (%)
              </label>
              <input
                type="number"
                value={closeRate}
                onChange={(e) => setCloseRate(Number(e.target.value))}
                min="0"
                max="100"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  fontSize: 16
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: '#374151'
              }}>
                Gross Margin (%)
              </label>
              <input
                type="number"
                value={grossMargin}
                onChange={(e) => setGrossMargin(Number(e.target.value))}
                min="0"
                max="100"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  fontSize: 16
                }}
              />
            </div>

            <div>
              <label style={{
                display: 'block',
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: '#374151'
              }}>
                Average Roofing Ticket ($)
              </label>
              <input
                type="number"
                value={ticketSize}
                onChange={(e) => setTicketSize(Number(e.target.value))}
                min="0"
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 8,
                  fontSize: 16
                }}
              />
            </div>
          </div>

          {/* Results */}
          <div style={{
            textAlign: 'center',
            padding: '32px',
            background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
            borderRadius: 16,
            marginBottom: 32,
            border: '2px solid #cbd5e1',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Subtle background pattern */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 30% 70%, rgba(59, 130, 246, 0.08) 0%, transparent 50%)',
              pointerEvents: 'none'
            }}></div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 24,
              position: 'relative',
              zIndex: 1
            }}>
              <div>
                <p style={{ fontSize: 16, color: '#475569', marginBottom: 8, fontWeight: 600 }}>Marketing Spend</p>
                <p style={{ fontSize: 24, fontWeight: 800, color: '#1e293b' }}>
                  ${totalCost.toLocaleString()}
                </p>
              </div>
              <div>
                <p style={{ fontSize: 16, color: '#475569', marginBottom: 8, fontWeight: 600 }}>Gross Sales</p>
                <p style={{ fontSize: 24, fontWeight: 800, color: '#1e293b' }}>
                  ${grossSales.toLocaleString()}
                </p>
              </div>
              <div>
                <p style={{ fontSize: 18, color: '#1d4ed8', marginBottom: 8, fontWeight: 700 }}>Net Profit</p>
                <p style={{ fontSize: 32, fontWeight: 800, color: '#1d4ed8' }}>
                  ${netProfit.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Projection Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 24
        }}>
          {/* 30 Days Projection */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 20,
            padding: '28px',
            boxShadow: '0 15px 45px rgba(0, 0, 0, 0.12), 0 6px 20px rgba(0, 0, 0, 0.08)',
            textAlign: 'center',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(8px)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 25px 60px rgba(0, 0, 0, 0.2), 0 10px 30px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 15px 45px rgba(0, 0, 0, 0.12), 0 6px 20px rgba(0, 0, 0, 0.08)';
          }}>
            <h4 style={{
              fontSize: 18,
              fontWeight: 600,
              marginBottom: 20,
              color: '#1f2937'
            }}>
              30 Days Projection
            </h4>
            
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', height: 250 }}>
              <div style={{ position: 'relative', width: 200, height: 200 }}>
                {/* CSS-based pie chart visualization */}
                <div 
                  style={{
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: `conic-gradient(
                      #3b82f6 0deg 22deg,
                      #1d4ed8 22deg 360deg
                    )`,
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 'calc(50% - 45px)',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'white',
                    transition: 'all 0.3s ease',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                  }}>
                    ${projections.monthly.spend.toLocaleString()}
                  </div>
                  <div style={{
                    position: 'absolute',
                    top: 'calc(50% + 45px)',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    fontWeight: 700,
                    color: 'white',
                    transition: 'all 0.3s ease',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                  }}>
                    ${projections.monthly.profit.toLocaleString()}
                  </div>
                </div>
                
                {/* Hover tooltip for Marketing Spend */}
                <div style={{
                  position: 'absolute',
                  top: '25%',
                  left: '10%',
                  background: 'rgba(59, 130, 246, 0.9)',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = '0';
                }}>
                  Marketing: ${projections.monthly.spend.toLocaleString()}
                </div>
                
                {/* Hover tooltip for Net Profit */}
                <div style={{
                  position: 'absolute',
                  bottom: '25%',
                  right: '10%',
                  background: 'rgba(29, 78, 216, 0.9)',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = '0';
                }}>
                  Profit: ${projections.monthly.profit.toLocaleString()}
                </div>
              </div>
            </div>
            
            <div style={{ fontSize: 14, textAlign: 'center', marginTop: 16 }}>
              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#1d4ed8',
                    flexShrink: 0
                  }}></div>
                  <span style={{ fontWeight: 700, color: '#3b82f6', fontSize: '17.5px' }}>Net Profit:</span> <span style={{ fontWeight: 700, color: '#3b82f6', fontSize: '20px' }}>${projections.monthly.profit.toLocaleString()}</span>
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#3b82f6',
                    flexShrink: 0
                  }}></div>
                  <span style={{ fontWeight: 600 }}>Marketing Spend:</span> ${projections.monthly.spend.toLocaleString()}
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 600 }}>Gross Sales:</span> ${projections.monthly.sales.toLocaleString()}
              </div>
            </div>
          </div>

          {/* 12 Months Projection */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 20,
            padding: '28px',
            boxShadow: '0 15px 45px rgba(0, 0, 0, 0.12), 0 6px 20px rgba(0, 0, 0, 0.08)',
            textAlign: 'center',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(8px)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 25px 60px rgba(0, 0, 0, 0.2), 0 10px 30px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 15px 45px rgba(0, 0, 0, 0.12), 0 6px 20px rgba(0, 0, 0, 0.08)';
          }}>
            <h4 style={{
              fontSize: 18,
              fontWeight: 600,
              marginBottom: 20,
              color: '#1f2937'
            }}>
              12 Months Projection
            </h4>
            
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', height: 250 }}>
              <div style={{ position: 'relative', width: 200, height: 200 }}>
                {/* CSS-based pie chart visualization */}
                <div 
                  style={{
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: `conic-gradient(
                      #3b82f6 0deg 22deg,
                      #1d4ed8 22deg 360deg
                    )`,
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 'calc(50% - 45px)',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'white',
                    transition: 'all 0.3s ease',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                  }}>
                    ${projections.yearly.spend.toLocaleString()}
                  </div>
                  <div style={{
                    position: 'absolute',
                    top: 'calc(50% + 45px)',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    fontWeight: 700,
                    color: 'white',
                    transition: 'all 0.3s ease',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                  }}>
                    ${projections.yearly.profit.toLocaleString()}
                  </div>
                </div>
                
                {/* Hover tooltip for Marketing Spend */}
                <div style={{
                  position: 'absolute',
                  top: '25%',
                  left: '10%',
                  background: 'rgba(59, 130, 246, 0.9)',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = '0';
                }}>
                  Marketing: ${projections.yearly.spend.toLocaleString()}
                </div>
                
                {/* Hover tooltip for Net Profit */}
                <div style={{
                  position: 'absolute',
                  bottom: '25%',
                  right: '10%',
                  background: 'rgba(29, 78, 216, 0.9)',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = '0';
                }}>
                  Profit: ${projections.yearly.profit.toLocaleString()}
                </div>
              </div>
            </div>
            
            <div style={{ fontSize: 14, textAlign: 'center', marginTop: 16 }}>
              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#1d4ed8',
                    flexShrink: 0
                  }}></div>
                  <span style={{ fontWeight: 700, color: '#3b82f6', fontSize: '17.5px' }}>Net Profit:</span> <span style={{ fontWeight: 700, color: '#3b82f6', fontSize: '20px' }}>${projections.yearly.profit.toLocaleString()}</span>
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#3b82f6',
                    flexShrink: 0
                  }}></div>
                  <span style={{ fontWeight: 600 }}>Marketing Spend:</span> ${projections.yearly.spend.toLocaleString()}
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 600 }}>Gross Sales:</span> ${projections.yearly.sales.toLocaleString()}
              </div>
            </div>
          </div>

          {/* 24 Months Projection */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            borderRadius: 20,
            padding: '28px',
            boxShadow: '0 15px 45px rgba(0, 0, 0, 0.12), 0 6px 20px rgba(0, 0, 0, 0.08)',
            textAlign: 'center',
            border: '2px solid rgba(255, 255, 255, 0.2)',
            backdropFilter: 'blur(8px)',
            transition: 'all 0.3s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 25px 60px rgba(0, 0, 0, 0.2), 0 10px 30px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 15px 45px rgba(0, 0, 0, 0.12), 0 6px 20px rgba(0, 0, 0, 0.08)';
          }}>
            <h4 style={{
              fontSize: 18,
              fontWeight: 600,
              marginBottom: 20,
              color: '#1f2937'
            }}>
              24 Months Projection
            </h4>
            
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', height: 250 }}>
              <div style={{ position: 'relative', width: 200, height: 200 }}>
                {/* CSS-based pie chart visualization */}
                <div 
                  style={{
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: `conic-gradient(
                      #3b82f6 0deg 22deg,
                      #1d4ed8 22deg 360deg
                    )`,
                    position: 'relative',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: 'calc(50% - 45px)',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'white',
                    transition: 'all 0.3s ease',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                  }}>
                    ${projections.twoYears.spend.toLocaleString()}
                  </div>
                  <div style={{
                    position: 'absolute',
                    top: 'calc(50% + 45px)',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 16,
                    fontWeight: 700,
                    color: 'white',
                    transition: 'all 0.3s ease',
                    textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
                  }}>
                    ${projections.twoYears.profit.toLocaleString()}
                  </div>
                </div>
                
                {/* Hover tooltip for Marketing Spend */}
                <div style={{
                  position: 'absolute',
                  top: '25%',
                  left: '10%',
                  background: 'rgba(59, 130, 246, 0.9)',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = '0';
                }}>
                  Marketing: ${projections.twoYears.spend.toLocaleString()}
                </div>
                
                {/* Hover tooltip for Net Profit */}
                <div style={{
                  position: 'absolute',
                  bottom: '25%',
                  right: '10%',
                  background: 'rgba(29, 78, 216, 0.9)',
                  color: 'white',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  opacity: 0,
                  transition: 'opacity 0.3s ease',
                  pointerEvents: 'none',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.opacity = '0';
                }}>
                  Profit: ${projections.twoYears.profit.toLocaleString()}
                </div>
              </div>
            </div>
            
            <div style={{ fontSize: 14, textAlign: 'center', marginTop: 16 }}>
              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#1d4ed8',
                    flexShrink: 0
                  }}></div>
                  <span style={{ fontWeight: 700, color: '#3b82f6', fontSize: '17.5px' }}>Net Profit:</span> <span style={{ fontWeight: 700, color: '#3b82f6', fontSize: '20px' }}>${projections.twoYears.profit.toLocaleString()}</span>
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <div style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: '#3b82f6',
                    flexShrink: 0
                  }}></div>
                  <span style={{ fontWeight: 600 }}>Marketing Spend:</span> ${projections.twoYears.spend.toLocaleString()}
                </div>
              </div>
              <div style={{ marginBottom: 8 }}>
                <span style={{ fontWeight: 600 }}>Gross Sales:</span> ${projections.twoYears.sales.toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Why Join Early Section */}
      <div style={{ maxWidth: 1000, margin: '0 auto 56px auto', background: 'white', borderRadius: 20, boxShadow: '0 8px 32px rgba(0,0,0,0.1)', padding: isMobileLayout ? 24 : 48, animation: 'fadeInUp 1.4s', position: 'relative', overflow: 'hidden' }}>
        
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h2 style={{ 
            fontSize: 48, 
            fontWeight: 900, 
            marginBottom: 48, 
            color: '#ffffff', 
            textAlign: 'center', 
            textShadow: '0 2px 4px rgba(0,0,0,0.4)',
            letterSpacing: '-0.01em',
            lineHeight: 1.1
          }}>Why Join Early?</h2>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
            {/* Top Row - Sections 1 & 2 */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ 
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)', 
                padding: 28, 
                borderRadius: 20, 
                border: '2px solid #22c55e',
                textAlign: 'center',
                position: 'relative',
                transform: 'rotate(-1deg)',
                boxShadow: '0 8px 25px rgba(34, 197, 94, 0.3)',
                transition: 'all 0.3s ease',
                width: '280px'
              }}>
                <div style={{ 
                  position: 'absolute', 
                  top: -15, 
                  left: '50%', 
                  transform: 'translateX(-50%)',
                  background: '#ffffff',
                  color: '#22c55e',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  fontWeight: 800,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}>1</div>
                <div style={{ fontWeight: 800, fontSize: 22, color: '#ffffff', marginTop: 20, marginBottom: 12, textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>Early Access</div>
                <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 500, opacity: 0.95 }}>Exclusive high-quality leads before anyone else</div>
              </div>
              
              <div style={{ 
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', 
                padding: 28, 
                borderRadius: 20, 
                border: '2px solid #3b82f6',
                textAlign: 'center',
                position: 'relative',
                transform: 'rotate(1deg)',
                boxShadow: '0 8px 25px rgba(59, 130, 246, 0.3)',
                transition: 'all 0.3s ease',
                width: '280px'
              }}>
                <div style={{ 
                  position: 'absolute', 
                  top: -15, 
                  left: '50%', 
                  transform: 'translateX(-50%)',
                  background: '#ffffff',
                  color: '#3b82f6',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  fontWeight: 800,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}>2</div>
                <div style={{ fontWeight: 800, fontSize: 22, color: '#ffffff', marginTop: 20, marginBottom: 12, textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>No Membership Fee</div>
                <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 500, opacity: 0.95 }}>Start without any upfront cost</div>
              </div>
            </div>
            
            {/* Bottom Row - Sections 3, 4 & 5 */}
            <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
              <div style={{ 
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', 
                padding: 28, 
                borderRadius: 20, 
                border: '2px solid #8b5cf6',
                textAlign: 'center',
                position: 'relative',
                transform: 'rotate(-0.5deg)',
                boxShadow: '0 8px 25px rgba(139, 92, 246, 0.3)',
                transition: 'all 0.3s ease',
                width: '280px'
              }}>
                <div style={{ 
                  position: 'absolute', 
                  top: -15, 
                  left: '50%', 
                  transform: 'translateX(-50%)',
                  background: '#ffffff',
                  color: '#8b5cf6',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  fontWeight: 800,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}>3</div>
                <div style={{ fontWeight: 800, fontSize: 22, color: '#ffffff', marginTop: 20, marginBottom: 12, textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>Priority Support</div>
                <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 500, opacity: 0.95 }}>Get fast, personalized help</div>
              </div>
              
              <div style={{ 
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', 
                padding: 28, 
                borderRadius: 20, 
                border: '2px solid #f59e0b',
                textAlign: 'center',
                position: 'relative',
                transform: 'rotate(0.5deg)',
                boxShadow: '0 8px 25px rgba(245, 158, 11, 0.3)',
                transition: 'all 0.3s ease',
                width: '280px'
              }}>
                <div style={{ 
                  position: 'absolute', 
                  top: -15, 
                  left: '50%', 
                  transform: 'translateX(-50%)',
                  background: '#ffffff',
                  color: '#f59e0b',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  fontWeight: 800,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}>4</div>
                <div style={{ fontWeight: 800, fontSize: 22, color: '#ffffff', marginTop: 20, marginBottom: 12, textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>Exclusive Offers</div>
                <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 500, opacity: 0.95 }}>Free ads & perks for founding users</div>
              </div>
              
              <div style={{ 
                background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)', 
                padding: 28, 
                borderRadius: 20, 
                border: '2px solid #ec4899',
                textAlign: 'center',
                position: 'relative',
                transform: 'rotate(-0.8deg)',
                boxShadow: '0 8px 25px rgba(236, 72, 153, 0.3)',
                transition: 'all 0.3s ease',
                width: '280px'
              }}>
                <div style={{ 
                  position: 'absolute', 
                  top: -15, 
                  left: '50%', 
                  transform: 'translateX(-50%)',
                  background: '#ffffff',
                  color: '#ec4899',
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  fontWeight: 800,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
                }}>5</div>
                <div style={{ fontWeight: 800, fontSize: 22, color: '#ffffff', marginTop: 20, marginBottom: 12, textShadow: '0 1px 2px rgba(0,0,0,0.2)' }}>Shape the Platform</div>
                <div style={{ color: '#ffffff', fontSize: 16, fontWeight: 500, opacity: 0.95 }}>Provide feedback to help build tools that work for you</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How CoreBids Works for Contractors Section */}
              <div style={{ maxWidth: 1000, margin: '0 auto 56px auto', background: '#f8f9fa', borderRadius: 20, border: '2px solid #f8f9fa', padding: 48, animation: 'fadeInUp 1.3s' }}>
        <h2 style={{ fontSize: 32, fontWeight: 800, marginBottom: 32, color: '#1e293b', textAlign: 'center' }}>CoreBids Contractor Process</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', gap: 32 }}>
          <div style={{ background: 'white', padding: 24, borderRadius: 16, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, marginRight: 16 }}>1</div>
              <div style={{ fontWeight: 700, fontSize: 20, color: '#2563eb' }}>Access New Projects</div>
            </div>
            <div style={{ color: '#374151', fontSize: 18, fontWeight: 500, paddingLeft: 48 }}>Easily find exterior renovation projects submitted by homeowners in your area.</div>
          </div>
          <div style={{ background: 'white', padding: 24, borderRadius: 16, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, marginRight: 16 }}>2</div>
              <div style={{ fontWeight: 700, fontSize: 20, color: '#2563eb' }}>Receive Accurate Data</div>
            </div>
            <div style={{ color: '#374151', fontSize: 18, fontWeight: 500, paddingLeft: 48 }}>Get precise measurements and clear images captured by proprietary software—no guesswork.</div>
          </div>
          <div style={{ background: 'white', padding: 24, borderRadius: 16, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, marginRight: 16 }}>3</div>
              <div style={{ fontWeight: 700, fontSize: 20, color: '#2563eb' }}>Bid with Confidence</div>
            </div>
            <div style={{ color: '#374151', fontSize: 18, fontWeight: 500, paddingLeft: 48 }}>Submit your competitive bid on projects posted through CoreBids, knowing all the details upfront.</div>
          </div>
          <div style={{ background: 'white', padding: 24, borderRadius: 16, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#2563eb', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, marginRight: 16 }}>4</div>
              <div style={{ fontWeight: 700, fontSize: 20, color: '#2563eb' }}>Win More Jobs</div>
            </div>
            <div style={{ color: '#374151', fontSize: 18, fontWeight: 500, paddingLeft: 48 }}>Secure projects before your competitors and grow your business with less hassle.</div>
          </div>
        </div>
      </div>



      {/* FAQ Section */}
      <div style={{ maxWidth: 1200, margin: '0 auto 56px auto', borderRadius: 20, padding: 48, animation: 'fadeInUp 1.5s' }}>
        <h2 style={{ fontSize: 32, fontWeight: 800, marginBottom: 32, color: '#1e293b', textAlign: 'center' }}>Frequently Asked Questions</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {FAQ_CATEGORIES.map((category, categoryIndex) => (
            <div key={categoryIndex} style={{ 
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', 
              borderRadius: 16, 
              border: '1px solid #e2e8f0',
              overflow: 'hidden'
            }}>
              {/* Category Header */}
              <div 
                style={{ 
                  padding: '20px 24px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  color: 'white',
                  fontWeight: 700,
                  fontSize: 18,
                  transition: 'background 0.2s ease'
                }}
                onClick={() => toggleCategory(category.title)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {category.title}
                </div>
                <div style={{ 
                  transition: 'all 0.3s ease',
                  fontSize: 20,
                  fontWeight: 700,
                  width: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {openCategories.has(category.title) ? '−' : '+'}
                </div>
              </div>
              
              {/* Category Questions */}
              {openCategories.has(category.title) && (
                <div style={{ padding: '0 24px 24px 24px' }}>
                  {category.faqs.map((faq, faqIndex) => (
                    <div key={faqIndex} style={{ marginTop: 16 }}>
                      <div 
                        style={{ 
                          cursor: 'pointer',
                          padding: '16px 20px',
                          background: 'white',
                          borderRadius: 12,
                          border: '1px solid #e2e8f0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => toggleQuestion(`${category.title}-${faq.q}`)}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f8fafc';
                          e.currentTarget.style.borderColor = '#2563eb';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.borderColor = '#e2e8f0';
                        }}
                      >
                        <div style={{ 
                          fontWeight: 600, 
                          fontSize: 16, 
                          color: '#1e293b',
                          flex: 1,
                          textAlign: 'left'
                        }}>
                          {faq.q}
                        </div>
                        <div style={{ 
                          transition: 'all 0.3s ease',
                          color: '#2563eb',
                          fontSize: 18,
                          fontWeight: 700,
                          width: 18,
                          height: 18,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {openQuestions.has(`${category.title}-${faq.q}`) ? '−' : '+'}
                        </div>
                      </div>
                      
                      {/* Answer Dropdown */}
                      {openQuestions.has(`${category.title}-${faq.q}`) && (
                        <div style={{ 
                          padding: '16px 20px',
                          background: '#f8fafc',
                          marginTop: 8,
                          borderRadius: 8,
                          border: '1px solid #e2e8f0',
                          animation: 'slideDown 0.3s ease'
                        }}>
                          <div style={{ 
                            color: '#374151', 
                            fontSize: 15, 
                            fontWeight: 500, 
                            lineHeight: 1.6 
                          }}>
                            {faq.a}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Contact Us & Social Media Section */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: 48, textAlign: 'center', animation: 'fadeInUp 1.6s' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 40, marginBottom: 32 }}>
          {SOCIALS.map(s => (
            <a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, transition: 'transform 0.18s', fontWeight: 700, fontSize: 18 }}>
              <span style={{ transition: 'transform 0.18s' }}>{s.icon}</span>
              <span style={{ color: '#374151', marginTop: 4 }}>{s.name}</span>
            </a>
          ))}
        </div>
        <div style={{ fontSize: 18, color: '#2563eb', fontWeight: 600 }}>
          <a href="mailto:info@corebids.com" style={{ color: '#2563eb', textDecoration: 'underline' }}>info@corebids.com</a>
        </div>
      </div>

      {/* Footer */}
      <div 
        style={{ 
          textAlign: 'center', 
          color: '#6b7280', 
          fontSize: 16, 
          marginTop: 56, 
          fontWeight: 500, 
          letterSpacing: 0.2
        }}
      >
        &copy; {new Date().getFullYear()} CoreBids. All rights reserved.
      </div>

      <style>{`
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(40px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes slideDown {
          0% { opacity: 0; transform: translateY(-10px); max-height: 0; }
          100% { opacity: 1; transform: translateY(0); max-height: 200px; }
        }
      `}</style>
    </div>
    </ResponsiveContainer>
  );
};

export default Waitlist; 